// ============================================
// VolumeSettingsMenu.jsx
// Settings popup for the bottom Volume bars (the existing VOLUME LANE canvas
// in FootprintChartCanvas). Shared menu shell (menu/ChartMenu), live edits,
// no APPLY — same as the other indicator menus.
//
// config shape (owned by the chart; other keys pass through):
//   { heightPct, fillColor, fillOpacity, posColor, negColor, outline, lineColor, lineOpacity }
//   posColor/negColor colour each bar by its delta; fillColor = zero/no-flow bars.
// heightPct: % of the chart body height the TALLEST visible bar reaches.
//   null = the classic fixed 120px lane (the original look).
// ============================================
import { useState } from 'react'
import { fmtVol } from './hooks/useVolumeAlerts'
import ChartMenu, { MenuRow, MenuNumber, MenuSwatch, MenuToggle, MenuDivider, MenuButton } from './menu/ChartMenu'

export const VOL_DEFAULTS = {
  heightPct: null,          // null = fixed 120px (unchanged look)
  fillColor: '#2b2b2b',     // was rgba(43,43,43,0.699)
  fillOpacity: 0.7,
  posColor: '#26a69a',      // voldelta-20261027: bar delta > 0 (buy green)
  negColor: '#ef5350',      // voldelta-20261027: bar delta < 0 (sell red)
  outline: true,            // dashed cap + sides
  lineColor: '#00ff2a',     // was #00ff2a27
  lineOpacity: 0.15,
}
export const VOL_PCT_MIN = 5
export const VOL_PCT_MAX = 80

// Volume alert row (volalert-20261028). new: prefilled with the clicked
// bar's volume, SET creates it. edit (right-click the bell): SAVE moves it,
// REMOVE deletes it. Typing never applies by itself, so a half-typed number
// can't fire the alert.
function VolAlertRow({ alert, onSet, onSave, onRemove, onClose }) {
  const [draft, setDraft] = useState(alert.level)
  const ok = Number.isFinite(draft) && draft > 0
  return (
    <>
      <MenuRow label="Alert" title={`Volume alert: fires when a later bar's volume reaches this (now ${fmtVol(draft)})`}>
        <MenuNumber label="VOL ≥" value={draft} float min={0} width={84} delay={0}
          title="Alert level (bar volume)" onCommit={(n) => setDraft(n)} />
        <MenuDivider />
        {alert.mode === 'new' ? (
          <MenuButton variant="ghost" disabled={!ok} title="Set the alert (drag the bell later to move it)"
            onClick={() => { if (ok) { onSet?.(draft); onClose?.() } }}>
            🔔 SET
          </MenuButton>
        ) : (
          <>
            <MenuButton variant="ghost" disabled={!ok || draft === alert.level} title="Move the alert to this level"
              onClick={() => { if (ok) { onSave?.(draft); onClose?.() } }}>
              SAVE
            </MenuButton>
            <MenuButton variant="ghost" title="Delete this volume alert"
              onClick={() => { onRemove?.(); onClose?.() }}>
              ✕ REMOVE
            </MenuButton>
          </>
        )}
      </MenuRow>
    </>
  )
}

export default function VolumeSettingsMenu({ show, x, y, openLeft, config, onUpdate, onClose, closeOnTouchOutside, alert, onAlertSet, onAlertSave, onAlertRemove }) {
  if (!show) return null
  const c = config
  const set = (patch) => onUpdate({ ...config, ...patch })
  const fixed = c.heightPct == null

  return (
    <ChartMenu anchor={{ x, y }} openLeft={openLeft} onClose={onClose} closeOnTouchOutside={closeOnTouchOutside}>
      {alert && (
        <VolAlertRow key={alert.mode === 'new' ? `n:${alert.t}:${alert.level}` : `e:${alert.id}`}
          alert={alert} onSet={onAlertSet} onSave={onAlertSave} onRemove={onAlertRemove} onClose={onClose} />
      )}
      {/* HEIGHT — % of the chart the tallest visible bar scales to. */}
      <MenuRow label="Height">
        <MenuNumber label="%" value={fixed ? '' : c.heightPct} min={VOL_PCT_MIN} max={VOL_PCT_MAX} clampMax width={48}
          title={`Tallest visible bar = this % of chart height (${VOL_PCT_MIN}–${VOL_PCT_MAX})`}
          onCommit={(n) => set({ heightPct: n })} />
        <MenuDivider />
        <MenuButton variant="ghost" on={fixed} title="Fixed 120px lane (original)"
          onClick={() => set({ heightPct: null })}>
          120PX
        </MenuButton>
      </MenuRow>
      <MenuRow label="Bars">
        <MenuSwatch label="+Δ" value={c.posColor} onChange={(v) => set({ posColor: v })} title="Positive delta colour" />
        <MenuSwatch label="−Δ" value={c.negColor} onChange={(v) => set({ negColor: v })} title="Negative delta colour" />
        <MenuSwatch label="0" value={c.fillColor} onChange={(v) => set({ fillColor: v })} title="Zero delta / no flow colour" />
        <MenuNumber label="OPACITY" value={c.fillOpacity} float step={0.05} min={0} max={1} clampMax width={44}
          onCommit={(n) => set({ fillOpacity: n })} />
      </MenuRow>
      <MenuRow label="Outline">
        <MenuToggle on={!!c.outline} onChange={() => set({ outline: !c.outline })}>{c.outline ? '✓' : '○'} Dashed</MenuToggle>
        <MenuSwatch value={c.lineColor} onChange={(v) => set({ lineColor: v })} title="Outline colour" />
        <MenuNumber label="OPACITY" value={c.lineOpacity} float step={0.05} min={0} max={1} clampMax width={44}
          onCommit={(n) => set({ lineOpacity: n })} />
      </MenuRow>
    </ChartMenu>
  )
}
